import { useMemo } from 'react';

import { useUsers } from '@/portainer/users/queries/useUsers';
import { useTeams } from '@/react/portainer/users/teams/queries';
import { useRbacRoles } from '@/react/portainer/users/RolesView/useRbacRoles';

import { TypedPolicy } from '../../../types';

import {
  DockerRBACFormValues,
  DockerRBACPolicyPayload,
  DOCKER_RBAC_FORM_DEFAULT_VALUES,
  UnifiedAccess,
} from './types';

/**
 * Hook to get initial values for Docker RBAC Policy form
 *
 * @param policy - Optional policy to load values from (for edit mode)
 * @returns Form initial values including name, environmentGroupIds, and accessList
 */
export function useDockerRBACInitialValues(
  policy?: TypedPolicy<unknown>
): DockerRBACFormValues {
  const usersQuery = useUsers(false);
  const users = useMemo(() => usersQuery.data || [], [usersQuery.data]);

  const teamsQuery = useTeams();
  const teams = useMemo(() => teamsQuery.data || [], [teamsQuery.data]);

  const rbacRolesQuery = useRbacRoles({
    select: (roles) =>
      roles.filter(
        (role) => role.Scope && (role.Scope.docker || role.Scope.swarm)
      ),
  });
  const roles = useMemo(() => rbacRolesQuery.data || [], [rbacRolesQuery.data]);

  return useMemo(() => {
    // Create mode: use defaults
    if (!policy) {
      return DOCKER_RBAC_FORM_DEFAULT_VALUES;
    }

    // Edit mode: deserialize from policy data
    const data = policy.Data;
    if (
      !data ||
      typeof data !== 'object' ||
      !('EnvironmentAccessPolicies' in data)
    ) {
      return {
        name: policy.Name,
        environmentGroupIds: policy.EnvironmentGroups ?? [],
        accessList: [],
      };
    }

    const { EnvironmentAccessPolicies } = data as DockerRBACPolicyPayload;
    if (!EnvironmentAccessPolicies) {
      return {
        name: policy.Name,
        environmentGroupIds: policy.EnvironmentGroups ?? [],
        accessList: [],
      };
    }

    const userAccess = Object.entries(
      EnvironmentAccessPolicies.UserAccessPolicies ?? {}
    )
      .map(([userId, policyData]) => {
        const id = parseInt(userId, 10);
        const user = users.find((u) => u.Id === id);
        if (!user) {
          return null;
        }
        const roleId = policyData.RoleId?.value ?? 0;
        const roleName = roles.find((r) => r.Id === roleId)?.Name || '';
        const item: UnifiedAccess = {
          Id: id,
          Type: 'user',
          Name: user.Username,
          Role: { Id: roleId, Name: roleName },
        };
        return item;
      })
      .filter((v): v is UnifiedAccess => v !== null);

    const teamAccess = Object.entries(
      EnvironmentAccessPolicies.TeamAccessPolicies ?? {}
    )
      .map(([teamId, policyData]) => {
        const id = parseInt(teamId, 10);
        const team = teams.find((t) => t.Id === id);
        if (!team) {
          return null;
        }
        const roleId = policyData.RoleId?.value ?? 0;
        const roleName = roles.find((r) => r.Id === roleId)?.Name || '';
        const item: UnifiedAccess = {
          Id: id,
          Type: 'team',
          Name: team.Name,
          Role: { Id: roleId, Name: roleName },
        };
        return item;
      })
      .filter((v): v is UnifiedAccess => v !== null);

    return {
      name: policy.Name,
      environmentGroupIds: policy.EnvironmentGroups ?? [],
      accessList: [...userAccess, ...teamAccess],
    };
  }, [policy, users, teams, roles]);
}
